import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import secureStorage from '../../lib/secureStorage';
import authedFetch from '../../lib/authedFetch';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  ReferenceLine, LineChart, Line,
} from 'recharts';
import { Users, ChevronRight, ArrowLeft, Check, Loader2, Activity, Download, TrendingUp, TrendingDown, ListPlus, FileText, Flag, Award, ArrowUpDown, Columns2, AlertTriangle, X, DollarSign, Bookmark, Trash2, Save, Share2, CalendarRange, Zap, Link2, Pencil } from 'lucide-react';
import ProgramCohortEditor from './ProgramCohortEditor';
import BudgetInlineEdit from './BudgetInlineEdit';
import { toast } from 'sonner';
import { addToBacklog, createGoalFromFocus } from '../health/HealthActionSpine';
import { cn } from '../../lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DEFAULT_CFG = {
  endpoint: '/api/lei-health/cohort-pillars',
  pdfEndpoint: '/api/lei-health/cohort-pillars/pdf',
  nounSingular: 'cohort',
  nounPlural: 'cohorts',
  title: 'Cohort LEI Pillar Health',
  chartTitle: 'Cohort LEI — pillar contribution vs benchmark',
  t: 'cohort',
  filterPrefix: 'cohort_filter',
  csvName: 'cohort-lei-comparison',
  emptyHint: 'Create cohorts in Segments, then import LEI measures to compare them here.',
  defaultSort: 'lei',
};

// Status band + colour for a 0-100 pillar/LEI score.
function band(score) {
  if (score == null) return { label: 'No data', color: '#94a3b8' };
  if (score >= 80) return { label: 'Strong', color: '#10b981' };
  if (score >= 65) return { label: 'Good', color: '#14b8a6' };
  if (score >= 50) return { label: 'Fair', color: '#f59e0b' };
  return { label: 'At risk', color: '#f43f5e' };
}

function ordinal(n) {
  const s = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
}

// Trend delta over the selected window (last `months` points).
function entityDelta(e, months) {
  const ft = e.trend || [];
  const t = months ? ft.slice(-months) : ft;
  return t.length > 1 ? Math.round((t[t.length - 1].score - t[0].score) * 10) / 10 : null;
}

// X-axis tick that wraps the entity name across up to 3 short lines so long
// cohort/program names don't truncate or collide with neighbours.
function WrappedTick({ x, y, payload, isDark }) {
  const words = String(payload.value).split(' ');
  const lines = [];
  let cur = '';
  words.forEach((w) => {
    if ((`${cur} ${w}`).trim().length > 13) { if (cur) lines.push(cur); cur = w; }
    else cur = (`${cur} ${w}`).trim();
  });
  if (cur) lines.push(cur);
  const shown = lines.slice(0, 3);
  return (
    <g transform={`translate(${x},${y + 8})`}>
      {shown.map((ln, i) => (
        <text key={i} x={0} y={i * 11} textAnchor="middle" fontSize={10} fill={isDark ? '#94a3b8' : '#64748b'}>
          {i === 2 && lines.length > 3 ? `${ln}…` : ln}
        </text>
      ))}
    </g>
  );
}

function fmtMoney(n) {
  if (n == null) return '—';
  if (n >= 1e6) return `$${(n / 1e6).toFixed(n >= 1e7 ? 0 : 1)}M`;
  if (n >= 1e3) return `$${Math.round(n / 1e3)}k`;
  return `$${n}`;
}

function fmtDate(s) {
  if (!s) return null;
  const d = new Date(s);
  if (isNaN(d)) return s;
  return d.toLocaleDateString(undefined, { month: 'short', year: 'numeric' });
}

function EntityTooltip({ active, payload, isDark, cfg }) {
  if (!active || !payload || !payload.length) return null;
  const row = payload[0]?.payload;
  if (!row) return null;
  return (
    <div
      className={cn('rounded-lg border p-3 text-xs shadow-xl', isDark ? 'bg-[#0b1220] border-[#22304d] text-slate-200' : 'bg-white border-slate-200 text-slate-700')}
      data-testid={`${cfg.t}-chart-tooltip`}
    >
      <div className="font-bold mb-1.5 flex items-center justify-between gap-4">
        <span>{row.name}</span>
        <span className="tabular-nums" style={{ color: band(row.overall).color }}>{row.overall} LEI</span>
      </div>
      <div className="space-y-1">
        {row._pillars.map((p) => (
          <div key={p.id} className="flex items-center justify-between gap-4">
            <span className="flex items-center gap-1.5">
              <span className="inline-block w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: p.color }} />
              {p.short_name}
            </span>
            <span className="tabular-nums">
              {p.score == null ? '—' : p.score}
              <span className={cn('ml-2', isDark ? 'text-slate-500' : 'text-slate-400')}>{p.pct_of_ideal != null ? `${p.pct_of_ideal}% of ideal` : ''}</span>
            </span>
          </div>
        ))}
      </div>
      <div className={cn('mt-1.5 pt-1.5 border-t text-[11px]', isDark ? 'border-[#22304d] text-slate-400' : 'border-slate-200 text-slate-500')}>
        Benchmark LEI: <span className="tabular-nums font-semibold">{row.benchmark}</span>
      </div>
    </div>
  );
}

// Compact 5-pillar breakdown card for a single entity (drill-in trigger).
function EntityCard({ entity, isDark, onSelect, rank, total, trendMonths, cfg, isEfficiencyLeader, avgCost, onEditCohorts, onSaved }) {
  const [editingBudget, setEditingBudget] = useState(false);
  const rows = [...entity.pillars].sort((a, b) => (b.score || 0) - (a.score || 0));
  const weakest = rows.reduce((min, p) => (p.score != null && (min == null || p.score < min.score) ? p : min), null);
  const ob = band(entity.overall_score);
  const fullTrend = entity.trend || [];
  const trend = trendMonths ? fullTrend.slice(-trendMonths) : fullTrend;
  const delta = trend.length > 1 ? Math.round((trend[trend.length - 1].score - trend[0].score) * 10) / 10 : null;
  const up = delta != null && delta >= 0;
  const slipping = delta != null && delta < 0;
  const trendColor = delta == null ? '#94a3b8' : (up ? '#10b981' : '#f43f5e');
  const gtp = entity.gap_to_peer;
  const topRank = rank === 1;
  const vsPeers = (avgCost && entity.cost_per_lei_point != null) ? Math.round(((avgCost - entity.cost_per_lei_point) / avgCost) * 100) : null;
  const endSoon = (() => {
    if (!entity.end_date) return false;
    const days = (new Date(entity.end_date).getTime() - Date.now()) / 86400000;
    return days >= 0 && days <= 90;
  })();
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => onSelect(entity.cohort_id)}
      onKeyDown={(e) => { if ((e.key === 'Enter' || e.key === ' ') && e.target === e.currentTarget) { e.preventDefault(); onSelect(entity.cohort_id); } }}
      className={cn(
        'w-full text-left rounded-2xl border p-4 transition-all group cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-teal-400',
        slipping ? 'border-rose-500/40' : (isDark ? 'border-[#22304d]' : 'border-slate-200'),
        isDark ? 'bg-[#0d1526] hover:border-[#31507d]' : 'bg-white hover:border-slate-300 hover:shadow-md',
      )}
      data-testid={`${cfg.t}-card-${entity.cohort_id}`}
    >
      <div className="flex items-center justify-between gap-2 mb-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <p className={cn('text-sm font-bold truncate', isDark ? 'text-slate-100' : 'text-slate-800')}>{entity.cohort_name}</p>
            {cfg.t === 'program' && onEditCohorts && (
              <span
                role="button"
                tabIndex={0}
                onClick={(e) => { e.stopPropagation(); onEditCohorts(entity); }}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); e.stopPropagation(); onEditCohorts(entity); } }}
                title="Link or unlink cohorts for this programme"
                className={cn('shrink-0 inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded-full border transition-colors',
                  isDark ? 'border-[#22304d] text-slate-300 hover:border-cyan-500/40 hover:text-cyan-200' : 'border-slate-200 text-slate-600 hover:border-teal-300 hover:text-teal-700 hover:bg-teal-50')}
                data-testid={`program-edit-cohorts-${entity.cohort_id}`}
              >
                <Link2 className="h-3 w-3" />{(entity.linked_cohort_ids || []).length} cohort{(entity.linked_cohort_ids || []).length === 1 ? '' : 's'}
              </span>
            )}
            {cfg.t === 'program' && (entity.linked_cohort_ids || []).length > 0 && (() => {
              const sizes = entity.roster_sizes || {};
              const linked = entity.linked_cohort_ids || [];
              const covered = linked.filter((c) => (sizes[c] || 0) > 0).length;
              const learners = linked.reduce((n, c) => n + (sizes[c] || 0), 0);
              const full = covered === linked.length, none = covered === 0;
              return (
                <span title={none ? 'No linked cohort has a roster yet — programme size unknown; weighting falls back to equal' : `${covered} of ${linked.length} linked cohorts have rosters · ${learners.toLocaleString()} learners${full ? ' — weighting fully trustworthy' : ' — partial coverage, missing cohorts get the median size'}`}
                  className={cn('shrink-0 inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded-full border', none ? (isDark ? 'border-[#22304d] text-slate-500' : 'border-slate-200 text-slate-400') : full ? (isDark ? 'border-emerald-500/40 text-emerald-300 bg-emerald-500/10' : 'border-emerald-200 text-emerald-700 bg-emerald-50') : (isDark ? 'border-amber-500/40 text-amber-300 bg-amber-500/10' : 'border-amber-200 text-amber-800 bg-amber-50'))}
                  data-testid={`program-roster-coverage-${entity.cohort_id}`}>
                  <Users className="h-2.5 w-2.5" />{none ? 'No rosters' : `${covered}/${linked.length} rosters · ${learners.toLocaleString()}`}
                </span>
              );
            })()}
            {cfg.t === 'program' && entity.lei_source === 'cohorts' && (
              <span title={entity.roster_weighted ? `Programme LEI is the roster-weighted blend of its cohorts (${Object.entries(entity.roster_sizes || {}).map(([, n]) => n).join(' / ')} learners)` : 'Programme LEI is the equal-weight blend of its cohorts — add rosters to weight by cohort size'}
                className={cn('shrink-0 text-[9px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded-full border', entity.roster_weighted ? (isDark ? 'border-emerald-500/40 text-emerald-300 bg-emerald-500/10' : 'border-emerald-200 text-emerald-700 bg-emerald-50') : (isDark ? 'border-[#22304d] text-slate-400' : 'border-slate-200 text-slate-500'))}
                data-testid={`program-lei-source-${entity.cohort_id}`}>
                {entity.roster_weighted ? 'Roster-weighted' : 'Equal-weight'}
              </span>
            )}
            {rank != null && (
              <span
                className={cn(
                  'shrink-0 inline-flex items-center gap-1 text-[10px] font-bold px-1.5 py-0.5 rounded-full border',
                  topRank
                    ? (isDark ? 'bg-amber-500/15 text-amber-300 border-amber-500/30' : 'bg-amber-50 text-amber-600 border-amber-200')
                    : (isDark ? 'bg-white/5 text-slate-300 border-[#22304d]' : 'bg-slate-50 text-slate-500 border-slate-200'),
                )}
                data-testid={`${cfg.t}-card-rank-${entity.cohort_id}`}
              >
                {topRank && <Award className="h-3 w-3" />}{ordinal(rank)} of {total}
              </span>
            )}
            {slipping && (
              <span className="shrink-0 inline-flex items-center gap-1 text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-rose-500/15 text-rose-500 border border-rose-500/30" data-testid={`${cfg.t}-card-slip-${entity.cohort_id}`}>
                <AlertTriangle className="h-3 w-3" /> Slipping
              </span>
            )}
            {isEfficiencyLeader && (
              <span className="shrink-0 inline-flex items-center gap-1 text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-emerald-500/15 text-emerald-500 border border-emerald-500/30" data-testid={`${cfg.t}-card-bestvalue-${entity.cohort_id}`}>
                <DollarSign className="h-3 w-3" /> Best value
              </span>
            )}
            {endSoon && (
              <span className="shrink-0 inline-flex items-center gap-1 text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-orange-500/15 text-orange-500 border border-orange-500/30" data-testid={`${cfg.t}-card-endingsoon-${entity.cohort_id}`}>
                <CalendarRange className="h-3 w-3" /> Ending soon
              </span>
            )}
          </div>
          <p className="text-[11px] font-semibold" style={{ color: ob.color }}>{ob.label}</p>
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          {(() => {
            let goalVal = null;
            try {
              const g = localStorage.getItem(`lei_forecast_goal_${cfg.t === 'program' ? 'prog' : 'cohort'}-${entity.cohort_id}`);
              if (g != null && g !== '' && !Number.isNaN(Number(g))) goalVal = Number(g);
            } catch (e) { /* ignore */ }
            if (!goalVal) return null;
            const pct = Math.max(0, Math.min(100, Math.round((entity.overall_score / goalVal) * 100)));
            const reached = entity.overall_score >= goalVal;
            const ringColor = reached ? '#10b981' : pct >= 75 ? '#0ea5e9' : '#f59e0b';
            const circ = 2 * Math.PI * 9;
            return (
              <span className="relative inline-flex items-center justify-center h-7 w-7" title={`Goal ${goalVal} · ${pct}% there`} data-testid={`${cfg.t}-card-goalring-${entity.cohort_id}`}>
                <svg viewBox="0 0 24 24" className="h-7 w-7 -rotate-90">
                  <circle cx="12" cy="12" r="9" fill="none" stroke={isDark ? '#22304d' : '#e2e8f0'} strokeWidth="3" />
                  <circle cx="12" cy="12" r="9" fill="none" stroke={ringColor} strokeWidth="3" strokeLinecap="round" strokeDasharray={`${(pct / 100) * circ} ${circ}`} />
                </svg>
                <span className="absolute text-[8px] font-bold" style={{ color: ringColor }}>{pct}</span>
              </span>
            );
          })()}
          <span className="text-2xl font-extrabold tabular-nums" style={{ color: ob.color }}>{entity.overall_score}</span>
          <ChevronRight className={cn('h-4 w-4 transition-colors', isDark ? 'text-slate-600 group-hover:text-slate-300' : 'text-slate-300 group-hover:text-slate-500')} />
        </div>
      </div>

      {cfg.t === 'program' && onEditCohorts && editingBudget && (
        <div className="mb-3"><BudgetInlineEdit entity={entity} isDark={isDark} onSaved={onSaved} onClose={() => setEditingBudget(false)} /></div>
      )}
      {(entity.investment != null || (cfg.t === 'program' && onEditCohorts)) && !editingBudget && (
        <div className={cn('flex items-center justify-between gap-2 mb-3 rounded-lg px-2.5 py-1.5 text-[11px] font-medium', isDark ? 'bg-white/5 text-slate-300' : 'bg-slate-50 text-slate-600')} data-testid={`${cfg.t}-card-roi-${entity.cohort_id}`}>
          <span className="inline-flex items-center gap-1.5"><DollarSign className="h-3.5 w-3.5 text-emerald-500" /> Invested <b>{fmtMoney(entity.investment)}</b></span>
          <span className="inline-flex items-center gap-1">Cost / LEI pt <b className="tabular-nums">{fmtMoney(entity.cost_per_lei_point)}</b>
            {cfg.t === 'program' && onEditCohorts && (
              <span role="button" tabIndex={0} onClick={(e) => { e.stopPropagation(); setEditingBudget(true); }} onKeyDown={(e) => { if (e.key === 'Enter') { e.stopPropagation(); setEditingBudget(true); } }} title="Edit budget and impact"
                className={cn('ml-1 inline-flex items-center justify-center w-5 h-5 rounded-md transition-colors', isDark ? 'text-slate-400 hover:text-cyan-200 hover:bg-white/10' : 'text-slate-400 hover:text-teal-700 hover:bg-teal-50')}
                data-testid={`program-budget-edit-${entity.cohort_id}`}><Pencil className="h-3 w-3" /></span>
            )}
          </span>
        </div>
      )}

      {vsPeers != null && (
        <p className={cn('-mt-1.5 mb-3 text-[11px] font-medium', Math.abs(vsPeers) < 3 ? (isDark ? 'text-slate-400' : 'text-slate-500') : (vsPeers > 0 ? 'text-emerald-500' : 'text-rose-500'))} data-testid={`${cfg.t}-card-vspeers-${entity.cohort_id}`}>
          {Math.abs(vsPeers) < 3
            ? 'About average cost per LEI point'
            : `${Math.abs(vsPeers)}% ${vsPeers > 0 ? 'cheaper' : 'pricier'} per point than average`}
        </p>
      )}

      {(entity.start_date || entity.end_date) && (
        <div className={cn('mb-3 text-[11px]', isDark ? 'text-slate-500' : 'text-slate-400')} data-testid={`${cfg.t}-card-timeline-${entity.cohort_id}`}>
          <div className="flex items-center gap-1.5">
            <CalendarRange className="h-3.5 w-3.5 shrink-0" />
            <span>{fmtDate(entity.start_date) || '—'} → <b className={isDark ? 'text-slate-300' : 'text-slate-600'}>{fmtDate(entity.end_date) || 'ongoing'}</b></span>
          </div>
          {entity.start_date && entity.end_date && (() => {
            const s = new Date(entity.start_date).getTime();
            const e = new Date(entity.end_date).getTime();
            const pct = Math.max(0, Math.min(100, Math.round(((Date.now() - s) / (e - s)) * 100)));
            return (
              <div className={cn('mt-1.5 h-1.5 rounded-full overflow-hidden', isDark ? 'bg-[#0b1220]' : 'bg-slate-100')} title={`${pct}% elapsed`}>
                <div className="h-full rounded-full" style={{ width: `${pct}%`, backgroundColor: pct >= 90 ? '#f97316' : (isDark ? '#38bdf8' : '#0ea5e9') }} />
              </div>
            );
          })()}
        </div>
      )}
      <div className="space-y-2">
        {rows.map((p) => {
          const c = band(p.score).color;
          const isWeak = weakest && p.id === weakest.id;
          const isGap = gtp && p.id === gtp.pillar_id;
          return (
            <div key={p.id} className="flex items-center gap-2">
              <span className={cn('w-28 shrink-0 text-[11px] font-medium truncate', isDark ? 'text-slate-400' : 'text-slate-500')}>{p.short_name}</span>
              <div className={cn('flex-1 h-1.5 rounded-full overflow-hidden', isDark ? 'bg-[#0b1220]' : 'bg-slate-100')}>
                <div className="h-full rounded-full" style={{ width: `${Math.min(100, p.score || 0)}%`, backgroundColor: c }} />
              </div>
              <span className="w-7 text-right text-[11px] font-bold tabular-nums" style={{ color: c }}>{p.score == null ? '—' : Math.round(p.score)}</span>
              {isWeak && <span className="shrink-0 text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-rose-500/15 text-rose-500 border border-rose-500/30">Constraint</span>}
              {!isWeak && isGap && <span className="shrink-0 text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-amber-500/15 text-amber-500 border border-amber-500/30">Peer gap</span>}
            </div>
          );
        })}
      </div>

      {gtp && (
        <div
          className={cn('mt-3 flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-[11px] font-medium', isDark ? 'bg-amber-500/10 text-amber-300' : 'bg-amber-50 text-amber-700')}
          data-testid={`${cfg.t}-card-gap-${entity.cohort_id}`}
        >
          <TrendingDown className="h-3.5 w-3.5 shrink-0" />
          <span>Biggest gap to peers: <b>{gtp.short_name}</b> (−{gtp.gap} vs {gtp.peer_avg})</span>
        </div>
      )}

      {trend.length > 1 && (
        <div className={cn('mt-3 pt-3 border-t', isDark ? 'border-[#22304d]' : 'border-slate-100')} data-testid={`${cfg.t}-card-trend-${entity.cohort_id}`}>
          <div className="flex items-center justify-between mb-1">
            <span className={cn('text-[10px] font-bold uppercase tracking-wider', isDark ? 'text-slate-500' : 'text-slate-400')}>LEI trend · {trend.length}mo</span>
            <span className="inline-flex items-center gap-1 text-[11px] font-bold tabular-nums" style={{ color: trendColor }}>
              {up ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
              {delta > 0 ? '+' : ''}{delta}
            </span>
          </div>
          <div className="h-8 -mx-1">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={trend} margin={{ top: 2, right: 4, left: 4, bottom: 2 }}>
                <YAxis hide domain={['dataMin - 3', 'dataMax + 3']} />
                <Line type="monotone" dataKey="score" stroke={trendColor} strokeWidth={1.75} dot={false} isAnimationActive={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}
    </div>
  );
}

// Side-by-side comparison of two entities' pillars.
function ComparePanel({ entities, isDark, cfg, onClose }) {
  const [a, setA] = useState(entities[0]?.cohort_id || '');
  const [b, setB] = useState(entities[1]?.cohort_id || entities[0]?.cohort_id || '');
  const ea = entities.find((e) => e.cohort_id === a);
  const eb = entities.find((e) => e.cohort_id === b);
  const selCls = cn('text-xs font-semibold rounded-lg border px-2 py-1.5', isDark ? 'bg-[#0b1220] border-[#22304d] text-slate-200' : 'bg-white border-slate-200 text-slate-700');
  const pillars = (ea?.pillars || eb?.pillars || []);
  return (
    <div className={cn('rounded-2xl border p-5 mb-4', isDark ? 'bg-[#0d1526] border-[#22304d]' : 'bg-white border-slate-200')} data-testid={`${cfg.t}-compare-panel`}>
      <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
        <div className="flex items-center gap-2 flex-wrap">
          <Columns2 className={cn('h-4 w-4', isDark ? 'text-cyan-300' : 'text-cyan-600')} />
          <span className={cn('text-[11px] font-bold uppercase tracking-[0.14em]', isDark ? 'text-slate-400' : 'text-slate-500')}>Compare two {cfg.nounPlural}</span>
          <select value={a} onChange={(e) => setA(e.target.value)} className={selCls} data-testid={`${cfg.t}-compare-select-a`}>
            {entities.map((e) => <option key={e.cohort_id} value={e.cohort_id}>{e.cohort_name}</option>)}
          </select>
          <span className={cn('text-xs', isDark ? 'text-slate-500' : 'text-slate-400')}>vs</span>
          <select value={b} onChange={(e) => setB(e.target.value)} className={selCls} data-testid={`${cfg.t}-compare-select-b`}>
            {entities.map((e) => <option key={e.cohort_id} value={e.cohort_id}>{e.cohort_name}</option>)}
          </select>
        </div>
        <button onClick={onClose} className={cn('inline-flex items-center gap-1 text-xs font-semibold', isDark ? 'text-slate-400 hover:text-slate-200' : 'text-slate-500 hover:text-slate-700')} data-testid={`${cfg.t}-compare-close`}>
          <X className="h-3.5 w-3.5" /> Close
        </button>
      </div>
      {ea && eb && (
        <div>
          <div className={cn('grid grid-cols-12 items-center gap-2 pb-2 mb-1 border-b text-[11px] font-bold uppercase tracking-wider', isDark ? 'border-[#22304d] text-slate-500' : 'border-slate-200 text-slate-400')}>
            <span className="col-span-4">Pillar</span>
            <span className="col-span-3 text-right truncate">{ea.cohort_name}</span>
            <span className="col-span-3 text-right truncate">{eb.cohort_name}</span>
            <span className="col-span-2 text-right">Diff</span>
          </div>
          <div className="grid grid-cols-12 items-center gap-2 py-2 text-sm font-bold border-b border-dashed" style={{ borderColor: isDark ? '#22304d' : '#e2e8f0' }} data-testid={`${cfg.t}-compare-overall`}>
            <span className={cn('col-span-4', isDark ? 'text-slate-200' : 'text-slate-700')}>Overall LEI</span>
            <span className="col-span-3 text-right tabular-nums" style={{ color: band(ea.overall_score).color }}>{ea.overall_score}</span>
            <span className="col-span-3 text-right tabular-nums" style={{ color: band(eb.overall_score).color }}>{eb.overall_score}</span>
            <DiffCell a={ea.overall_score} b={eb.overall_score} isDark={isDark} />
          </div>
          {pillars.map((meta) => {
            const pa = ea.pillars.find((x) => x.id === meta.id);
            const pb = eb.pillars.find((x) => x.id === meta.id);
            return (
              <div key={meta.id} className={cn('grid grid-cols-12 items-center gap-2 py-2 text-sm', isDark ? 'text-slate-300' : 'text-slate-600')} data-testid={`${cfg.t}-compare-row-${meta.id}`}>
                <span className="col-span-4 flex items-center gap-2 min-w-0">
                  <span className="inline-block w-2.5 h-2.5 rounded-sm shrink-0" style={{ backgroundColor: meta.color }} />
                  <span className="truncate">{meta.short_name}</span>
                </span>
                <span className="col-span-3 text-right tabular-nums font-semibold" style={{ color: band(pa?.score).color }}>{pa?.score ?? '—'}</span>
                <span className="col-span-3 text-right tabular-nums font-semibold" style={{ color: band(pb?.score).color }}>{pb?.score ?? '—'}</span>
                <DiffCell a={pa?.score} b={pb?.score} isDark={isDark} />
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function DiffCell({ a, b, isDark }) {
  if (a == null || b == null) return <span className={cn('col-span-2 text-right text-xs', isDark ? 'text-slate-600' : 'text-slate-300')}>—</span>;
  const d = Math.round((a - b) * 10) / 10;
  const color = d === 0 ? (isDark ? '#64748b' : '#94a3b8') : (d > 0 ? '#10b981' : '#f43f5e');
  return (
    <span className="col-span-2 text-right tabular-nums font-bold text-xs" style={{ color }}>
      {d > 0 ? '+' : ''}{d}
    </span>
  );
}

// Detailed pillar view for a single drilled-in entity.
function EntityDetail({ entity, isDark, onBack, navigate, organizationId, cfg, onBudget }) {
  const rows = [...entity.pillars].sort((a, b) => (b.score || 0) - (a.score || 0));
  const weakest = rows.reduce((min, p) => (p.score != null && (min == null || p.score < min.score) ? p : min), null);
  const gtp = entity.gap_to_peer;
  const daysToEnd = entity.end_date ? Math.round((new Date(entity.end_date).getTime() - Date.now()) / 86400000) : null;
  const endSoon = daysToEnd != null && daysToEnd >= 0 && daysToEnd <= 90;

  function buildWeakestFocus() {
    return {
      engine: 'lei',
      entityId: weakest.id,
      entityLabel: `${weakest.short_name} — ${entity.cohort_name}`,
      pillarId: weakest.id,
      score: weakest.score,
      target: 100,
      gapPct: Math.max(0, 100 - Math.round(weakest.score || 0)),
      overallScore: entity.overall_score,
      granularity: 'pillar',
      organizationId: organizationId && organizationId !== 'all' ? organizationId : undefined,
    };
  }
  const addWeakestToBacklog = () => { if (weakest) addToBacklog(buildWeakestFocus(), navigate); };
  const createGoalForEntity = () => { if (weakest) createGoalFromFocus(buildWeakestFocus(), navigate); };
  const createRenewalTask = () => {
    createGoalFromFocus({
      engine: 'lei',
      entityId: entity.cohort_id,
      entityLabel: `Renew: ${entity.cohort_name}`,
      pillarId: entity.cohort_id,
      score: entity.overall_score,
      target: 100,
      overallScore: entity.overall_score,
      granularity: cfg.nounSingular,
      organizationId: organizationId && organizationId !== 'all' ? organizationId : undefined,
    }, navigate);
  };
  const setBudget = () => {
    const cur = entity.investment != null ? String(entity.investment) : '';
    const val = window.prompt(`Set an annual budget for “${entity.cohort_name}” ($). Leave blank to clear.`, cur);
    if (val === null) return;
    onBudget && onBudget(entity.cohort_id, val.trim() === '' ? null : Number(val.replace(/[^0-9.]/g, '')));
  };

  return (
    <div data-testid={`${cfg.t}-detail-view`}>
      <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
        <button
          onClick={onBack}
          className={cn('inline-flex items-center gap-1.5 text-sm font-semibold', isDark ? 'text-slate-300 hover:text-white' : 'text-slate-600 hover:text-slate-900')}
          data-testid={`${cfg.t}-detail-back`}
        >
          <ArrowLeft className="h-4 w-4" /> All {cfg.nounPlural}
        </button>
        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={setBudget}
            className={cn('inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg border transition-colors', isDark ? 'border-[#22304d] text-slate-300 hover:bg-white/5' : 'border-slate-200 text-slate-600 hover:bg-slate-50')}
            data-testid={`${cfg.t}-detail-set-budget`}
          >
            <DollarSign className="h-3.5 w-3.5" /> {entity.investment != null ? 'Edit budget' : 'Set budget'}
          </button>
          <button
            onClick={createGoalForEntity}
            className={cn('inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg border transition-colors', isDark ? 'border-amber-500/40 text-amber-300 hover:bg-amber-500/10' : 'border-amber-300 text-amber-700 hover:bg-amber-50')}
            data-testid={`${cfg.t}-detail-create-goal`}
          >
            <Flag className="h-3.5 w-3.5" /> Create a goal for this {cfg.nounSingular}
          </button>
          <button
            onClick={addWeakestToBacklog}
            className={cn('inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg border transition-colors', isDark ? 'border-teal-500/40 text-teal-300 hover:bg-teal-500/10' : 'border-teal-300 text-teal-700 hover:bg-teal-50')}
            data-testid={`${cfg.t}-detail-add-backlog`}
          >
            <ListPlus className="h-3.5 w-3.5" /> Add weakest pillar to backlog
          </button>
        </div>
      </div>
      <div className={cn('rounded-2xl border p-5', isDark ? 'bg-[#0d1526] border-[#22304d]' : 'bg-white border-slate-200')}>
        <div className="flex items-center justify-between gap-3 mb-1">
          <div>
            <h3 className={cn('text-lg font-bold', isDark ? 'text-slate-100' : 'text-slate-800')}>{entity.cohort_name}</h3>
            <p className={cn('text-xs', isDark ? 'text-slate-500' : 'text-slate-400')}>Click a pillar to model its impact on the LEI → KPI → ROI chain</p>
            {(entity.start_date || entity.end_date) && (
              <p className={cn('inline-flex items-center gap-1.5 text-xs mt-1.5', isDark ? 'text-slate-400' : 'text-slate-500')} data-testid={`${cfg.t}-detail-timeline`}>
                <CalendarRange className="h-3.5 w-3.5" /> {fmtDate(entity.start_date) || '—'} → <b>{fmtDate(entity.end_date) || 'ongoing'}</b>
                {entity.status && <span className={cn('ml-1 px-1.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide', isDark ? 'bg-white/5 text-slate-300' : 'bg-slate-100 text-slate-500')}>{entity.status}</span>}
              </p>
            )}
          </div>
          <div className="text-right">
            <p className="text-3xl font-extrabold tabular-nums" style={{ color: band(entity.overall_score).color }}>{entity.overall_score}</p>
            <p className={cn('text-[11px] uppercase font-bold tracking-wider', isDark ? 'text-slate-500' : 'text-slate-400')}>{cfg.nounSingular} LEI</p>
          </div>
        </div>

        {entity.investment != null && (
          <div className="grid grid-cols-3 gap-3 mt-4" data-testid={`${cfg.t}-detail-roi`}>
            {[
              { label: 'Invested', value: fmtMoney(entity.investment), icon: DollarSign },
              { label: 'Cost / LEI point', value: fmtMoney(entity.cost_per_lei_point) },
              { label: 'Value / +1%', value: entity.impact_per_percent != null ? fmtMoney(entity.impact_per_percent) : '—' },
            ].map((m) => (
              <div key={m.label} className={cn('rounded-xl border p-3', isDark ? 'bg-[#0b1220] border-[#22304d]' : 'bg-slate-50 border-slate-200')}>
                <p className={cn('text-[10px] font-bold uppercase tracking-wider', isDark ? 'text-slate-500' : 'text-slate-400')}>{m.label}</p>
                <p className={cn('text-lg font-extrabold tabular-nums mt-0.5', isDark ? 'text-slate-100' : 'text-slate-800')}>{m.value}</p>
              </div>
            ))}
          </div>
        )}

        <div className="mt-4 space-y-1">
          {rows.map((p) => {
            const c = band(p.score).color;
            const isWeak = weakest && p.id === weakest.id;
            const isGap = gtp && p.id === gtp.pillar_id;
            return (
              <button
                key={p.id}
                onClick={() => navigate && navigate(`/portfolio?focusReason=${cfg.t}-weak-link&focusEngine=lei&focusMeasure=${encodeURIComponent(p.id)}&focusLabel=${encodeURIComponent(p.short_name)}`)}
                className={cn('w-full group flex items-center gap-3 px-3 py-2.5 rounded-xl transition-colors text-left', isDark ? 'hover:bg-white/[0.04]' : 'hover:bg-slate-50')}
                data-testid={`${cfg.t}-detail-pillar-${p.id}`}
              >
                <span className={cn('shrink-0 w-8 h-8 rounded-lg flex items-center justify-center', isDark ? 'bg-[#0b1220] ring-1 ring-[#22304d]' : 'bg-slate-50 ring-1 ring-slate-200')}>
                  <Activity className="h-4 w-4" style={{ color: p.color }} />
                </span>
                <div className="min-w-0 w-40 sm:w-52 shrink-0">
                  <p className={cn('text-sm font-semibold truncate', isDark ? 'text-slate-100' : 'text-slate-800')}>{p.short_name}</p>
                  <p className="text-[11px] font-medium" style={{ color: c }}>{band(p.score).label}{p.pct_of_ideal != null ? ` · ${p.pct_of_ideal}% of ideal` : ''}</p>
                </div>
                <div className={cn('flex-1 h-2 rounded-full overflow-hidden', isDark ? 'bg-[#0b1220]' : 'bg-slate-100')}>
                  <div className="h-full rounded-full" style={{ width: `${Math.min(100, p.score || 0)}%`, backgroundColor: c }} />
                </div>
                {isGap && <span className="shrink-0 text-[10px] font-bold px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-500 border border-amber-500/30">−{gtp.gap} vs peers</span>}
                {isWeak && <span className="shrink-0 text-[10px] font-bold px-2 py-0.5 rounded-full bg-rose-500/15 text-rose-500 border border-rose-500/30">Constraint</span>}
                <span className="shrink-0 w-8 text-right text-lg font-bold tabular-nums" style={{ color: c }}>{p.score == null ? '—' : Math.round(p.score)}</span>
                <ChevronRight className={cn('h-4 w-4 transition-colors shrink-0', isDark ? 'text-slate-600 group-hover:text-slate-300' : 'text-slate-300 group-hover:text-slate-500')} />
              </button>
            );
          })}
        </div>
      </div>

      {endSoon && (
        <div className={cn('flex items-start gap-2.5 rounded-2xl border p-4 mt-4', isDark ? 'bg-orange-500/10 border-orange-500/30 text-orange-200' : 'bg-orange-50 border-orange-200 text-orange-800')} data-testid={`${cfg.t}-detail-renewal`}>
          <CalendarRange className="h-4 w-4 mt-0.5 shrink-0 text-orange-500" />
          <div className="text-xs">
            <p className="font-bold">Review for renewal</p>
            <p className={cn('mt-0.5', isDark ? 'text-orange-200/80' : 'text-orange-700/90')}>
              This {cfg.nounSingular} ends in {daysToEnd} day{daysToEnd === 1 ? '' : 's'} ({fmtDate(entity.end_date)}). Decide now whether to renew, extend, or hand off so momentum isn't lost.
            </p>
            <button
              onClick={createRenewalTask}
              className={cn('inline-flex items-center gap-1.5 mt-2 text-xs font-bold px-2.5 py-1 rounded-lg border transition-colors', isDark ? 'border-orange-500/40 text-orange-200 hover:bg-orange-500/15' : 'border-orange-300 text-orange-700 hover:bg-orange-100')}
              data-testid={`${cfg.t}-detail-renewal-task`}
            >
              <Flag className="h-3.5 w-3.5" /> Create renewal task
            </button>
          </div>
        </div>
      )}

      {/* Program → Cohorts drill: which cohorts make up this program's LEI */}
      {Array.isArray(entity.child_cohorts) && entity.child_cohorts.length > 0 && (
        <div className={cn('rounded-2xl border p-5 mt-4', isDark ? 'bg-[#0d1526] border-[#22304d]' : 'bg-white border-slate-200')} data-testid={`${cfg.t}-detail-cohorts`}>
          <div className="flex items-center gap-2 mb-1">
            <Users className={cn('h-4 w-4', isDark ? 'text-slate-400' : 'text-slate-500')} />
            <h4 className={cn('text-sm font-bold', isDark ? 'text-slate-100' : 'text-slate-800')}>Cohorts in this {cfg.nounSingular}</h4>
          </div>
          <p className={cn('text-xs mb-3', isDark ? 'text-slate-500' : 'text-slate-400')}>Individual cohort LEI — the lowest is pulling the {cfg.nounSingular} average down.</p>
          <div className="space-y-1.5">
            {entity.child_cohorts.map((cc, i) => {
              const c = band(cc.overall_score).color;
              const lowest = i === entity.child_cohorts.length - 1 && entity.child_cohorts.length > 1;
              return (
                <div key={cc.id} className="flex items-center gap-3" data-testid={`${cfg.t}-detail-cohort-${cc.id}`}>
                  <span className={cn('w-40 sm:w-56 shrink-0 text-sm font-medium truncate', isDark ? 'text-slate-200' : 'text-slate-700')}>{cc.name}</span>
                  <div className={cn('flex-1 h-2 rounded-full overflow-hidden', isDark ? 'bg-[#0b1220]' : 'bg-slate-100')}>
                    <div className="h-full rounded-full" style={{ width: `${Math.min(100, cc.overall_score || 0)}%`, backgroundColor: c }} />
                  </div>
                  {lowest && <span className="shrink-0 text-[10px] font-bold px-2 py-0.5 rounded-full bg-rose-500/15 text-rose-500 border border-rose-500/30">Lowest</span>}
                  <span className="w-9 text-right text-sm font-bold tabular-nums" style={{ color: c }}>{cc.overall_score}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

export default function CohortComparisonView({ organizationId, isDark, navigate, config }) {
  const cfg = { ...DEFAULT_CFG, ...(config || {}) };
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [selected, setSelected] = useState(null);
  const [drill, setDrill] = useState(null);
  const [trendMonths, setTrendMonths] = useState(6);
  const [sortBy, setSortBy] = useState(cfg.defaultSort || 'lei');
  const [compareOpen, setCompareOpen] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [editingProgram, setEditingProgram] = useState(null);

  const filterKey = `${cfg.filterPrefix}_${organizationId || 'default'}`;

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const token = secureStorage.getItem('session_token');
      const params = new URLSearchParams();
      if (organizationId && organizationId !== 'all') params.append('organization_id', organizationId);
      const res = await authedFetch(`${API_URL}${cfg.endpoint}?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const d = await res.json();
        setData(d);
        const ids = (d.cohorts || []).map((c) => c.cohort_id);
        let restored = null;
        try {
          const saved = JSON.parse(localStorage.getItem(filterKey) || 'null');
          if (Array.isArray(saved)) {
            const keep = saved.filter((id) => ids.includes(id));
            if (keep.length) restored = keep;
          }
        } catch (e) { /* ignore */ }
        setSelected(restored || ids);
      } else {
        setData({ cohorts: [], pillars: [], median_overall: 0, benchmark_overall: 0 });
      }
    } catch (e) {
      setData({ cohorts: [], pillars: [], median_overall: 0, benchmark_overall: 0 });
    } finally {
      setLoading(false);
    }
  }, [organizationId, filterKey, cfg.endpoint]);

  useEffect(() => { fetchData(); setDrill(null); }, [fetchData]);

  useEffect(() => {
    if (selected != null) {
      try { localStorage.setItem(filterKey, JSON.stringify(selected)); } catch (e) { /* ignore */ }
    }
  }, [selected, filterKey]);

  const pillars = data?.pillars || [];
  const allEntities = useMemo(() => data?.cohorts || [], [data]);
  const [statusFilter, setStatusFilter] = useState('all');
  const passStatus = useCallback((e) => statusFilter === 'all' || (e.status || '').toLowerCase() === statusFilter, [statusFilter]);
  const selectedSet = useMemo(() => new Set(selected || []), [selected]);
  const visibleEntities = allEntities.filter((c) => selectedSet.has(c.cohort_id) && passStatus(c));
  const rankMap = useMemo(() => {
    const m = {};
    allEntities.forEach((c, i) => { m[c.cohort_id] = i + 1; });
    return m;
  }, [allEntities]);
  const hasRoi = useMemo(() => allEntities.some((e) => e.cost_per_lei_point != null), [allEntities]);
  const efficiencyLeaderId = useMemo(() => {
    let best = null;
    allEntities.forEach((e) => {
      if (e.cost_per_lei_point != null && (best == null || e.cost_per_lei_point < best.cost_per_lei_point)) best = e;
    });
    return best ? best.cohort_id : null;
  }, [allEntities]);
  const avgCost = useMemo(() => {
    const vals = allEntities.map((e) => e.cost_per_lei_point).filter((v) => v != null);
    return vals.length ? vals.reduce((a, b) => a + b, 0) / vals.length : null;
  }, [allEntities]);
  const hasDates = useMemo(() => allEntities.some((e) => e.end_date), [allEntities]);
  const hasStatus = useMemo(() => allEntities.some((e) => e.status), [allEntities]);
  const statusOptions = useMemo(() => {
    const s = new Set(allEntities.map((e) => (e.status || '').toLowerCase()).filter(Boolean));
    return Array.from(s);
  }, [allEntities]);

  // Cards to render, sorted per the chosen momentum/LEI/name/ROI option.
  const displayEntities = useMemo(() => {
    const list = [...(visibleEntities.length ? visibleEntities : allEntities)];
    const costOf = (e) => (e.cost_per_lei_point == null ? Infinity : e.cost_per_lei_point);
    if (sortBy === 'momentum') {
      list.sort((x, y) => (entityDelta(y, trendMonths) ?? -Infinity) - (entityDelta(x, trendMonths) ?? -Infinity));
    } else if (sortBy === 'name') {
      list.sort((x, y) => x.cohort_name.localeCompare(y.cohort_name));
    } else if (sortBy === 'roi_eff') {
      list.sort((x, y) => costOf(x) - costOf(y));
    } else if (sortBy === 'roi_cost') {
      list.sort((x, y) => (costOf(y) === Infinity ? -1 : costOf(y)) - (costOf(x) === Infinity ? -1 : costOf(x)));
    } else if (sortBy === 'ending') {
      const endOf = (e) => (e.end_date ? new Date(e.end_date).getTime() : Infinity);
      list.sort((x, y) => endOf(x) - endOf(y));
    } else {
      list.sort((x, y) => y.overall_score - x.overall_score);
    }
    return list;
  }, [visibleEntities, allEntities, sortBy, trendMonths]);

  const slippingCount = useMemo(
    () => (visibleEntities.length ? visibleEntities : allEntities).filter((e) => (entityDelta(e, trendMonths) ?? 0) < 0).length,
    [visibleEntities, allEntities, trendMonths],
  );

  const chartData = useMemo(() => visibleEntities.map((c) => {
    const row = {
      name: c.cohort_name,
      overall: c.overall_score,
      benchmark: data?.benchmark_overall || 0,
      momentum: entityDelta(c, trendMonths),
      _pillars: c.pillars,
    };
    c.pillars.forEach((p) => {
      row[p.id] = p.contribution;
      row[`${p.id}_b`] = p.benchmark_contribution;
    });
    return row;
  }), [visibleEntities, data, trendMonths]);
  const lastPillarId = pillars.length ? pillars[pillars.length - 1].id : null;

  const toggleEntity = (id) => {
    setSelected((prev) => {
      const s = new Set(prev);
      if (s.has(id)) s.delete(id); else s.add(id);
      return Array.from(s);
    });
  };
  const allSelected = allEntities.length > 0 && (selected || []).length === allEntities.length;
  const toggleAll = () => setSelected(allSelected ? [] : allEntities.map((c) => c.cohort_id));

  const median = data?.median_overall || 0;

  const renderMomentum = (props) => {
    const { x, y, width, index } = props;
    const m = chartData[index]?.momentum;
    if (m == null || x == null) return null;
    const up = m >= 0;
    const color = m === 0 ? '#94a3b8' : (up ? '#10b981' : '#f43f5e');
    return (
      <text x={x + width / 2} y={y - 7} textAnchor="middle" fontSize={10} fontWeight={700} fill={color}>
        {up ? '▲' : '▼'} {m > 0 ? '+' : ''}{m}
      </text>
    );
  };

  // Saved views: named filter + sort + trend-window combos.
  const viewsKey = `saved_views_${cfg.t}_${organizationId || 'default'}`;
  const [savedViews, setSavedViews] = useState([]);
  useEffect(() => {
    try { setSavedViews(JSON.parse(localStorage.getItem(viewsKey) || '[]')); } catch (e) { setSavedViews([]); }
  }, [viewsKey]);
  const persistViews = (next) => {
    setSavedViews(next);
    try { localStorage.setItem(viewsKey, JSON.stringify(next)); } catch (e) { /* ignore */ }
  };
  const saveCurrentView = () => {
    const name = (window.prompt('Name this view (e.g. "My plants, by momentum")') || '').trim();
    if (!name) return;
    const view = { name, selected, sortBy, trendMonths };
    const next = [...savedViews.filter((v) => v.name !== name), view];
    persistViews(next);
    toast.success(`Saved view “${name}”`);
  };
  const applyView = (v) => {
    if (!v) return;
    const ids = allEntities.map((c) => c.cohort_id);
    setSelected((v.selected || ids).filter((id) => ids.includes(id)));
    setSortBy(v.sortBy || 'lei');
    setTrendMonths(v.trendMonths || 6);
    toast.success(`Applied “${v.name}”`);
  };
  const deleteView = (name) => persistViews(savedViews.filter((v) => v.name !== name));

  const saveBudget = async (entityId, investment) => {
    try {
      const token = secureStorage.getItem('session_token');
      const res = await authedFetch(`${API_URL}/api/lei-health/entity-budget`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ kind: cfg.nounSingular, id: entityId, investment, organization_id: organizationId }),
      });
      if (!res.ok) throw new Error('budget');
      toast.success('Budget updated');
      fetchData();
    } catch (e) {
      toast.error('Could not update budget');
    }
  };
  const setDefaultView = (name) => {
    persistViews(savedViews.map((v) => ({ ...v, default: v.name === name ? !v.default : false })));
  };

  const shareView = async (v) => {
    const payload = { selected: v.selected, sortBy: v.sortBy, trendMonths: v.trendMonths, tab: cfg.t };
    const enc = btoa(unescape(encodeURIComponent(JSON.stringify(payload))));
    const url = `${window.location.origin}${window.location.pathname}?view=${enc}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success('Shareable link copied', { description: `“${v.name}” — anyone with the link opens this exact view.` });
    } catch (e) {
      toast.message('Copy this link', { description: url });
    }
  };

  // Apply a shared view from the URL (?view=...), else the default saved view,
  // once data has loaded.
  const urlAppliedRef = useRef(false);
  useEffect(() => {
    if (urlAppliedRef.current || !data || !allEntities.length) return;
    const enc = new URLSearchParams(window.location.search).get('view');
    if (enc) {
      try {
        const payload = JSON.parse(decodeURIComponent(escape(atob(enc))));
        if (payload.tab && payload.tab !== cfg.t) return;
        const ids = allEntities.map((c) => c.cohort_id);
        if (Array.isArray(payload.selected)) setSelected(payload.selected.filter((id) => ids.includes(id)));
        if (payload.sortBy) setSortBy(payload.sortBy);
        if (payload.trendMonths) setTrendMonths(payload.trendMonths);
        urlAppliedRef.current = true;
        toast.success('Opened shared view');
        return;
      } catch (e) { /* ignore */ }
    }
    const def = savedViews.find((v) => v.default);
    if (def) {
      const ids = allEntities.map((c) => c.cohort_id);
      setSelected((def.selected || ids).filter((id) => ids.includes(id)));
      setSortBy(def.sortBy || 'lei');
      setTrendMonths(def.trendMonths || 6);
      urlAppliedRef.current = true;
    }
  }, [data, allEntities, cfg.t, savedViews]);

  const downloadCsv = () => {
    if (!data) return;
    const header = [cfg.nounSingular, 'LEI', ...(hasRoi ? ['Invested', 'Cost per LEI pt'] : []), ...pillars.map((p) => p.short_name), 'Biggest gap to peers'];
    const lines = displayEntities.map((c) => {
      const gtp = c.gap_to_peer;
      return [
        c.cohort_name, c.overall_score,
        ...(hasRoi ? [c.investment != null ? c.investment : '', c.cost_per_lei_point != null ? c.cost_per_lei_point : ''] : []),
        ...pillars.map((p) => { const pp = c.pillars.find((x) => x.id === p.id); return pp && pp.score != null ? pp.score : ''; }),
        gtp ? `${gtp.short_name} (-${gtp.gap} vs ${gtp.peer_avg})` : 'On par with peers',
      ];
    });
    const csv = [header, ...lines].map((r) => r.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `${cfg.csvName}.csv`; a.click();
    URL.revokeObjectURL(url);
    toast.success('CSV exported');
  };

  const downloadPdf = async () => {
    setPdfLoading(true);
    try {
      const token = secureStorage.getItem('session_token');
      const params = new URLSearchParams();
      if (organizationId && organizationId !== 'all') params.append('organization_id', organizationId);
      const res = await authedFetch(`${API_URL}${cfg.pdfEndpoint}?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('pdf');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `${cfg.csvName}.pdf`; a.click();
      URL.revokeObjectURL(url);
      toast.success('PDF exported');
    } catch (e) {
      toast.error('Could not generate PDF');
    } finally {
      setPdfLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20" data-testid={`${cfg.t}-view-loading`}>
        <Loader2 className={cn('h-6 w-6 animate-spin', isDark ? 'text-slate-400' : 'text-slate-500')} />
      </div>
    );
  }

  const unlinked = data?.unlinked || [];
  const unlinkedNote = unlinked.length > 0 && (
    <div className={cn('flex items-start gap-2 rounded-xl border px-4 py-2.5 text-xs', isDark ? 'bg-amber-500/10 border-amber-500/30 text-amber-200' : 'bg-amber-50 border-amber-200 text-amber-900')} data-testid={`${cfg.t}-unlinked-note`}>
      <Users className="h-4 w-4 shrink-0 mt-0.5" />
      <div>
        <span className="font-semibold">{unlinked.length} {unlinked.length === 1 ? cfg.nounSingular : cfg.nounPlural} not shown</span> — no linked cohorts or measures yet:{' '}
        <span className="opacity-90">{unlinked.slice(0, 6).map((u) => u.name).join(', ')}{unlinked.length > 6 ? ` +${unlinked.length - 6} more` : ''}</span>.
        {navigate && <button onClick={() => navigate('/segments?tab=programs')} className="ml-2 font-semibold underline-offset-2 hover:underline" data-testid={`${cfg.t}-unlinked-link`}>Link cohorts in Segments →</button>}
      </div>
    </div>
  );

  if (!allEntities.length) {
    return (
      <div className="space-y-3">
      {unlinkedNote}
      <div className={cn('text-center py-16 rounded-2xl border', isDark ? 'bg-[#0d1526] border-[#22304d] text-slate-400' : 'bg-white border-slate-200 text-slate-500')} data-testid={`${cfg.t}-view-empty`}>
        <Users className="h-8 w-8 mx-auto mb-3 opacity-50" />
        <p className="text-sm">No {cfg.nounSingular} data found for this organization.</p>
        <p className="text-xs mt-1 opacity-70">{cfg.emptyHint}</p>
      </div>
      </div>
    );
  }

  const drillEntity = drill ? allEntities.find((c) => c.cohort_id === drill) : null;
  const selCls = cn('text-xs font-semibold rounded-lg border px-2 py-1.5 outline-none', isDark ? 'bg-[#0b1220] border-[#22304d] text-slate-200' : 'bg-white border-slate-200 text-slate-700');

  return (
    <div className="space-y-6" data-testid={`${cfg.t}-comparison-view`}>
      {cfg.t === 'program' && (
        <ProgramCohortEditor open={!!editingProgram} onOpenChange={(o) => { if (!o) setEditingProgram(null); }} program={editingProgram} organizationId={organizationId} isDark={isDark} onSaved={fetchData} />
      )}
      {unlinkedNote}
      {/* Efficiency headline — best learning ROI */}
      {hasRoi && efficiencyLeaderId && (() => {
        const leader = allEntities.find((e) => e.cohort_id === efficiencyLeaderId);
        if (!leader) return null;
        return (
          <button
            onClick={() => setDrill(leader.cohort_id)}
            className={cn('w-full flex items-center gap-3 rounded-2xl border p-4 text-left transition-colors', isDark ? 'bg-emerald-500/10 border-emerald-500/30 hover:bg-emerald-500/15' : 'bg-emerald-50 border-emerald-200 hover:bg-emerald-100/70')}
            data-testid={`${cfg.t}-efficiency-headline`}
          >
            <span className={cn('shrink-0 w-10 h-10 rounded-xl flex items-center justify-center', isDark ? 'bg-emerald-500/20' : 'bg-emerald-100')}>
              <Zap className="h-5 w-5 text-emerald-500" />
            </span>
            <div className="min-w-0">
              <p className={cn('text-[11px] font-bold uppercase tracking-[0.14em]', isDark ? 'text-emerald-300/80' : 'text-emerald-700/80')}>Best learning ROI</p>
              <p className={cn('text-sm font-bold truncate', isDark ? 'text-slate-100' : 'text-slate-800')}>
                {leader.cohort_name} — <span className="text-emerald-500">{fmtMoney(leader.cost_per_lei_point)}</span> per LEI point
              </p>
            </div>
            <ChevronRight className={cn('ml-auto h-4 w-4 shrink-0', isDark ? 'text-emerald-300/70' : 'text-emerald-600/70')} />
          </button>
        );
      })()}

      {/* Export + saved-views toolbar */}
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2 flex-wrap">
          <Bookmark className={cn('h-4 w-4', isDark ? 'text-slate-400' : 'text-slate-500')} />
          {savedViews.length === 0 && (
            <span className={cn('text-xs', isDark ? 'text-slate-500' : 'text-slate-400')}>No saved views yet</span>
          )}
          {savedViews.map((v) => (
            <span key={v.name} className={cn('inline-flex items-center gap-1 text-xs font-semibold pl-2.5 pr-1.5 py-1 rounded-full border', v.default ? (isDark ? 'bg-amber-500/10 border-amber-500/30 text-amber-200' : 'bg-amber-50 border-amber-200 text-amber-700') : (isDark ? 'bg-white/5 border-[#22304d] text-slate-200' : 'bg-slate-50 border-slate-200 text-slate-600'))} data-testid={`${cfg.t}-saved-view-${v.name.replace(/\s+/g, '-').toLowerCase()}`}>
              <button onClick={() => setDefaultView(v.name)} className={cn('rounded-full p-0.5', isDark ? 'hover:bg-white/10' : 'hover:bg-slate-200')} aria-label="Set default view" title="Set as default" data-testid={`${cfg.t}-default-view`}>
                <Award className={cn('h-3 w-3', v.default ? 'text-amber-400' : (isDark ? 'text-slate-500' : 'text-slate-400'))} />
              </button>
              <button onClick={() => applyView(v)} className="hover:underline" data-testid={`${cfg.t}-apply-view`}>{v.name}</button>
              <button onClick={() => shareView(v)} className={cn('rounded-full p-0.5', isDark ? 'hover:bg-white/10' : 'hover:bg-slate-200')} aria-label="Share view" data-testid={`${cfg.t}-share-view`}><Share2 className="h-3 w-3" /></button>
              <button onClick={() => deleteView(v.name)} className={cn('rounded-full p-0.5', isDark ? 'hover:bg-white/10' : 'hover:bg-slate-200')} aria-label="Delete view"><Trash2 className="h-3 w-3" /></button>
            </span>
          ))}
          <button
            onClick={saveCurrentView}
            className={cn('inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-lg border transition-colors', isDark ? 'border-[#22304d] text-slate-300 hover:bg-white/5' : 'border-slate-200 text-slate-600 hover:bg-slate-50')}
            data-testid={`${cfg.t}-save-view`}
          >
            <Save className="h-3.5 w-3.5" /> Save view
          </button>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={downloadCsv}
            className={cn('inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg border transition-colors', isDark ? 'border-[#22304d] text-slate-300 hover:bg-white/5' : 'border-slate-200 text-slate-600 hover:bg-slate-50')}
            data-testid={`${cfg.t}-export-csv`}
          >
            <Download className="h-3.5 w-3.5" /> Export CSV
          </button>
          <button
            onClick={downloadPdf}
            disabled={pdfLoading}
            className={cn('inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg border transition-colors disabled:opacity-60', isDark ? 'border-cyan-500/40 text-cyan-300 hover:bg-cyan-500/10' : 'border-cyan-300 text-cyan-700 hover:bg-cyan-50')}
            data-testid={`${cfg.t}-export-pdf`}
          >
            {pdfLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <FileText className="h-3.5 w-3.5" />} Export PDF
          </button>
        </div>
      </div>

      {/* Filter chips */}
      <div className={cn('rounded-2xl border p-4', isDark ? 'bg-[#0d1526] border-[#22304d]' : 'bg-white border-slate-200')}>
        <div className="flex items-center justify-between gap-3 mb-3">
          <div className="flex items-center gap-2">
            <Users className={cn('h-4 w-4', isDark ? 'text-slate-400' : 'text-slate-500')} />
            <span className={cn('text-[11px] font-bold uppercase tracking-[0.14em]', isDark ? 'text-slate-400' : 'text-slate-500')}>Filter {cfg.nounPlural}</span>
          </div>
          <button
            onClick={toggleAll}
            className={cn('text-xs font-semibold px-2.5 py-1 rounded-lg transition-colors', isDark ? 'text-cyan-300 hover:bg-white/5' : 'text-cyan-600 hover:bg-cyan-50')}
            data-testid={`${cfg.t}-filter-toggle-all`}
          >
            {allSelected ? 'Clear all' : 'Select all'}
          </button>
        </div>
        <div className="flex flex-wrap gap-2">
          {allEntities.map((c) => {
            const on = selectedSet.has(c.cohort_id);
            return (
              <button
                key={c.cohort_id}
                onClick={() => toggleEntity(c.cohort_id)}
                className={cn(
                  'inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full border transition-all',
                  on
                    ? (isDark ? 'bg-cyan-500/15 border-cyan-500/40 text-cyan-200' : 'bg-cyan-50 border-cyan-300 text-cyan-700')
                    : (isDark ? 'bg-transparent border-[#22304d] text-slate-500 hover:text-slate-300' : 'bg-transparent border-slate-200 text-slate-400 hover:text-slate-600'),
                )}
                data-testid={`${cfg.t}-filter-chip-${c.cohort_id}`}
              >
                {on && <Check className="h-3 w-3" />}
                {c.cohort_name}
              </button>
            );
          })}
        </div>
      </div>

      {/* Stacked bar chart */}
      <div className={cn('rounded-2xl border p-5', isDark ? 'bg-[#0d1526] border-[#22304d]' : 'bg-white border-slate-200')} data-testid={`${cfg.t}-stacked-chart`}>
        <div className="flex items-start justify-between gap-3 mb-1 flex-wrap">
          <div>
            <h3 className={cn('text-sm font-bold', isDark ? 'text-slate-100' : 'text-slate-800')}>{cfg.chartTitle}</h3>
            <p className={cn('text-xs mt-0.5', isDark ? 'text-slate-500' : 'text-slate-400')}>Each bar stacks weighted pillar contributions to the {cfg.nounSingular} LEI; the faded bar is the benchmark.</p>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            {pillars.map((p) => (
              <span key={p.id} className={cn('inline-flex items-center gap-1.5 text-[11px] font-medium', isDark ? 'text-slate-300' : 'text-slate-600')}>
                <span className="inline-block w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: p.color }} />{p.short_name}
              </span>
            ))}
          </div>
        </div>
        <div className="h-96 mt-3">
          {chartData.length === 0 ? (
            <div className={cn('flex items-center justify-center h-full text-sm', isDark ? 'text-slate-500' : 'text-slate-400')} data-testid={`${cfg.t}-chart-none-selected`}>
              Select at least one {cfg.nounSingular} to compare.
            </div>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData} margin={{ top: 28, right: 16, left: 0, bottom: 24 }} barGap={2} barCategoryGap="18%">
                <CartesianGrid strokeDasharray="3 3" stroke={isDark ? '#1f2a44' : '#eef2f7'} vertical={false} />
                <XAxis dataKey="name" interval={0} height={54} tickLine={false} tick={<WrappedTick isDark={isDark} />} />
                <YAxis domain={[0, 100]} tick={{ fontSize: 11, fill: isDark ? '#94a3b8' : '#64748b' }} />
                <Tooltip cursor={{ fill: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.03)' }} content={<EntityTooltip isDark={isDark} cfg={cfg} />} />
                {pillars.map((p) => (
                  <Bar
                    key={p.id}
                    dataKey={p.id}
                    stackId="actual"
                    fill={p.color}
                    radius={p.id === lastPillarId ? [4, 4, 0, 0] : [0, 0, 0, 0]}
                    maxBarSize={46}
                    label={p.id === lastPillarId ? renderMomentum : undefined}
                  />
                ))}
                {pillars.map((p) => (
                  <Bar key={`${p.id}_b`} dataKey={`${p.id}_b`} stackId="benchmark" fill={p.color} fillOpacity={0.28} radius={p.id === lastPillarId ? [4, 4, 0, 0] : [0, 0, 0, 0]} maxBarSize={46} />
                ))}
                <ReferenceLine
                  y={median}
                  stroke={isDark ? '#e2e8f0' : '#0f172a'}
                  strokeDasharray="6 4"
                  strokeWidth={1.5}
                  label={{ value: `Avg ${median}`, position: 'insideTopRight', fill: isDark ? '#e2e8f0' : '#0f172a', fontSize: 10, fontWeight: 700 }}
                />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
        <p className={cn('text-[11px] mt-2', isDark ? 'text-slate-500' : 'text-slate-400')}>
          Left bar = actual · faded right bar = benchmark ({data?.benchmark_overall || 0}) · dashed line = average LEI across all {cfg.nounPlural} · <span className="font-semibold">▲/▼ above each bar = LEI change over the selected {trendMonths}-month trend window</span>.
        </p>
      </div>

      {/* Entity LEI Pillar Health — summarised per entity with drill-down */}
      <div data-testid={`${cfg.t}-pillar-health-section`}>
        <div className="flex items-start justify-between gap-3 mb-1 flex-wrap">
          <div>
            <h3 className={cn('text-sm font-bold', isDark ? 'text-slate-100' : 'text-slate-800')}>{cfg.title}</h3>
            <p className={cn('text-xs mb-2', isDark ? 'text-slate-500' : 'text-slate-400')}>A pillar breakdown for each {cfg.nounSingular} — click any card to drill into its detailed pillar view.</p>
          </div>
          {!drillEntity && (
            <div className="flex items-center gap-2 flex-wrap">
              {hasStatus && (
                <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className={selCls} data-testid={`${cfg.t}-status-filter`}>
                  <option value="all">Status: All</option>
                  {statusOptions.map((s) => (
                    <option key={s} value={s}>{`Status: ${s.charAt(0).toUpperCase()}${s.slice(1)}`}</option>
                  ))}
                </select>
              )}
              <div className="inline-flex items-center gap-1.5">
                <ArrowUpDown className={cn('h-3.5 w-3.5', isDark ? 'text-slate-500' : 'text-slate-400')} />
                <select value={sortBy} onChange={(e) => setSortBy(e.target.value)} className={selCls} data-testid={`${cfg.t}-sort-select`}>
                  <option value="lei">Sort: LEI (high → low)</option>
                  <option value="momentum">Sort: Momentum (improving first)</option>
                  <option value="name">Sort: Name (A → Z)</option>
                  {hasRoi && <option value="roi_eff">Sort: Efficiency (cheapest / LEI pt)</option>}
                  {hasRoi && <option value="roi_cost">Sort: Least efficient ($ / LEI pt)</option>}
                  {hasDates && <option value="ending">Sort: Ending soonest</option>}
                </select>
              </div>
              <button
                onClick={() => setCompareOpen((v) => !v)}
                className={cn('inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1.5 rounded-lg border transition-colors', compareOpen ? (isDark ? 'bg-cyan-500/15 border-cyan-500/40 text-cyan-200' : 'bg-cyan-50 border-cyan-300 text-cyan-700') : (isDark ? 'border-[#22304d] text-slate-300 hover:bg-white/5' : 'border-slate-200 text-slate-600 hover:bg-slate-50'))}
                data-testid={`${cfg.t}-compare-toggle`}
              >
                <Columns2 className="h-3.5 w-3.5" /> Compare two
              </button>
              <div className={cn('inline-flex items-center rounded-lg border p-0.5 text-[11px] font-semibold', isDark ? 'border-[#22304d] bg-[#0b1220]' : 'border-slate-200 bg-slate-50')} data-testid={`${cfg.t}-trend-range-toggle`}>
                <span className={cn('px-2', isDark ? 'text-slate-500' : 'text-slate-400')}>Trend</span>
                {[6, 12].map((m) => (
                  <button
                    key={m}
                    onClick={() => setTrendMonths(m)}
                    className={cn('px-2.5 py-1 rounded-md transition-colors', trendMonths === m ? (isDark ? 'bg-cyan-500/20 text-cyan-200' : 'bg-white text-cyan-700 shadow-sm') : (isDark ? 'text-slate-400 hover:text-slate-200' : 'text-slate-500 hover:text-slate-700'))}
                    data-testid={`${cfg.t}-trend-range-${m}`}
                  >
                    {m}mo
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Slip alert banner */}
        {!drillEntity && slippingCount > 0 && (
          <div className={cn('flex items-center gap-2 rounded-xl px-3 py-2 mb-4 text-xs font-medium', isDark ? 'bg-rose-500/10 text-rose-300 border border-rose-500/20' : 'bg-rose-50 text-rose-700 border border-rose-100')} data-testid={`${cfg.t}-slip-banner`}>
            <AlertTriangle className="h-4 w-4 shrink-0" />
            <span>{slippingCount} {cfg.nounSingular}{slippingCount > 1 ? 's' : ''} slipping over the last {trendMonths} months — check the flagged cards below.</span>
          </div>
        )}

        {drillEntity ? (
          <EntityDetail entity={drillEntity} isDark={isDark} onBack={() => setDrill(null)} navigate={navigate} organizationId={organizationId} cfg={cfg} onBudget={saveBudget} />
        ) : (
          <>
            {compareOpen && <ComparePanel entities={displayEntities.length ? displayEntities : allEntities} isDark={isDark} cfg={cfg} onClose={() => setCompareOpen(false)} />}
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              {displayEntities.map((c) => (
                <EntityCard key={c.cohort_id} entity={c} isDark={isDark} onSelect={setDrill} rank={rankMap[c.cohort_id]} total={allEntities.length} trendMonths={trendMonths} cfg={cfg} isEfficiencyLeader={c.cohort_id === efficiencyLeaderId} avgCost={avgCost} onEditCohorts={cfg.t === 'program' ? setEditingProgram : undefined} onSaved={fetchData} />
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// Small nudge for the Learning Health overview: how many programs end soon.
export function EndingSoonNudge({ organizationId, isDark, navigate, thresholdDays = 90 }) {
  const [count, setCount] = useState(0);
  const [names, setNames] = useState([]);
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const token = secureStorage.getItem('session_token');
        const params = new URLSearchParams();
        if (organizationId && organizationId !== 'all') params.append('organization_id', organizationId);
        const res = await authedFetch(`${API_URL}/api/lei-health/program-pillars?${params}`, { headers: { Authorization: `Bearer ${token}` } });
        if (!res.ok) return;
        const d = await res.json();
        const soon = (d.cohorts || []).filter((p) => {
          if (!p.end_date) return false;
          const days = (new Date(p.end_date).getTime() - Date.now()) / 86400000;
          return days >= 0 && days <= thresholdDays;
        });
        if (alive) { setCount(soon.length); setNames(soon.map((s) => s.cohort_name)); }
      } catch (e) { /* ignore */ }
    })();
    return () => { alive = false; };
  }, [organizationId, thresholdDays]);
  if (!count) return null;
  return (
    <button
      onClick={() => navigate && navigate('/lei-health/programs')}
      className={cn('w-full flex items-center gap-2.5 rounded-xl border px-4 py-2.5 mb-4 text-left transition-colors', isDark ? 'bg-orange-500/10 border-orange-500/30 text-orange-200 hover:bg-orange-500/15' : 'bg-orange-50 border-orange-200 text-orange-800 hover:bg-orange-100/70')}
      data-testid="lei-ending-soon-nudge"
    >
      <CalendarRange className="h-4 w-4 shrink-0 text-orange-500" />
      <span className="text-xs font-medium">
        <b>{count} program{count > 1 ? 's' : ''} ending soon</b> — {names.slice(0, 2).join(', ')}{names.length > 2 ? ` +${names.length - 2} more` : ''}. Review renewals →
      </span>
    </button>
  );
}
